/**
 * @file Linked child item sections for parent-board detail views.
 */

import { appData, appDataDetails } from '../../app/state/state.js';
import { sortCanonicalTaskItems } from '../board/itemSortUtils.js';
import {
    convertIdsToNamesForEditing,
    sanitizeHTML,
} from "../utils/contentUtils.js";
import {
    findItem,
    getItemType,
} from "../utils/itemLookup.js";
import {
    formatDate,
} from "../utils/dateUtils.js";
import {
    getTaskDisplayUserIds,
    getTaskQuickCloseStatusConfig,
} from "../utils/taskDisplay.js";
import {
    getUserDisplayNameById,
} from "../utils/userDisplay.js";
import {
    isColorDark,
} from "../utils/displayUtils.js";
import {
    isItemClosed,
} from "../utils/closedState.js";

const CLOSED_TASK_SUMMARY_BUTTON_CLASSES = [
    'closed-linked-tasks-summary',
    'mt-2',
    'flex',
    'min-h-8',
    'w-full',
    'items-center',
    'gap-1.5',
    'text-left',
    'text-sm',
    'transition-colors',
    'focus:outline-none',
    'focus:text-[var(--dfc-brand-primary)]',
].join(' ');

const LINKED_TASKS_UI_SESSION = {
    parentId: null,
    closedExpanded: false,
};

/**
 * Clears linked-task-only UI state when the detail session ends.
 */
export function resetLinkedTaskUiSession() {
    LINKED_TASKS_UI_SESSION.parentId = null;
    LINKED_TASKS_UI_SESSION.closedExpanded = false;
}

function getLinkedTaskUiSession(parentId) {
    if (LINKED_TASKS_UI_SESSION.parentId !== parentId) {
        LINKED_TASKS_UI_SESSION.parentId = parentId;
        LINKED_TASKS_UI_SESSION.closedExpanded = false;
    }
    return LINKED_TASKS_UI_SESSION;
}

/**
 * Renders the linked task list section for parent-board detail items.
 * @param {string} parentId Parent item id.
 * @returns {string} Linked task section HTML.
 */
export function renderTaskSection(parentId) {
    if (!parentId) {return '';}

    const priorities = appData?.config?.priorities || [];
    const tasks = appDataDetails?.tasks || [];
    const openTasks = sortCanonicalTaskItems(
        tasks.filter(t => t.status && !isItemClosed(t)),
        tasks,
    );
    const closedTasks = sortCanonicalTaskItems(
        tasks.filter(t => t.status && isItemClosed(t)),
        tasks,
    );
    const uiSession = getLinkedTaskUiSession(parentId);
    const closedExpanded = uiSession.closedExpanded;
    const taskStatuses = appData?.config?.taskStatus || [];
    const itemType = getItemType(parentId);

    const createTaskListItem = (task) => {
        const priority = priorities.find(p => p.id === task.priority) || {};
        const status = taskStatuses.find(s => s.id === task.status) || { name: 'Unknown', color: '#ccc' };
        const statusTextColor = isColorDark(status.color) ? 'text-white' : 'text-gray-800';

        const taskTypesArr = task.types || [];
        let typeBadgeHtml = '';
        if (taskTypesArr.length > 0) {
            const primaryType = (appData?.config?.taskTypes || []).find(t => t.id === taskTypesArr[0]);
            if (primaryType) {
                const typeStyle = primaryType.color ? `background-color: ${primaryType.color}; color: ${isColorDark(primaryType.color) ? 'white' : '#1f2937'};` : '';
                typeBadgeHtml = `<span class="px-2 py-0.5 rounded text-xs sm:ml-2 flex-shrink-0" style="${typeStyle}" title="${primaryType.name}">${primaryType.name}</span>`;
            }
        }

        const displayUserNames = getTaskDisplayUserIds(task)
            .map((id) => getUserDisplayNameById(id).split(' ')[0] || '?')
            .join(', ');

        let checklistHtml = '';
        const clMap = task.checklist || {};
        const incompleteItems = Object.entries(clMap).map(([id, data]) => ({ id, ...data }))
            .filter(i => !i.isCompleted)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        if (incompleteItems.length > 0) {
            const nextItem = incompleteItems[0];
            checklistHtml = `
            <div class="flex items-center min-w-0" title="Next action: ${sanitizeHTML(nextItem.text)}">
                 <input type="checkbox" class="kanban-checklist-toggle form-checkbox h-3.5 w-3.5 rounded border-gray-300 text-[var(--dfc-brand-primary)] focus:ring-[var(--dfc-brand-primary-ring)] cursor-pointer flex-shrink-0"
                        data-task-id="${task.id}" data-item-id="${nextItem.id}" ${nextItem.isCompleted ? 'checked' : ''} onclick="event.stopPropagation(); window.handleKanbanChecklistToggle(event, '${task.id}', 'task', '${nextItem.id}')">
                 <span class="ml-1.5 text-xs text-gray-500 dark:text-gray-400 truncate">${sanitizeHTML(nextItem.text)}</span>
            </div>
            `;
        }

        let latestNoteHtml = '';
        if (task.latestNote && task.latestNote.text) {
            const rawText = task.latestNote.text;
            const textWithNames = convertIdsToNamesForEditing(rawText);
            const snippet = textWithNames.substring(0, 150) + (textWithNames.length > 150 ? '...' : '');
            latestNoteHtml = `<div class="flex items-center min-w-0 truncate text-gray-500 dark:text-gray-400" title="${sanitizeHTML(textWithNames)}"><span class="material-symbols-outlined text-[14px] mr-1.5">chat_bubble</span><span class="text-xs truncate italic">${sanitizeHTML(snippet)}</span></div>`;
        } else if (task.notes && task.notes.length > 0) {
            const latestNote = [...task.notes].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
            if (latestNote) {
                const rawText = latestNote.text || '';
                const textWithNames = convertIdsToNamesForEditing(rawText);
                const snippet = textWithNames.substring(0, 150) + (textWithNames.length > 150 ? '...' : '');
                latestNoteHtml = `<div class="flex items-center min-w-0 truncate text-gray-500 dark:text-gray-400" title="${sanitizeHTML(textWithNames)}"><span class="material-symbols-outlined text-[14px] mr-1.5">chat_bubble</span><span class="text-xs truncate italic">${sanitizeHTML(snippet)}</span></div>`;
            }
        }

        const quickCloseButtonHtml = getTaskQuickCloseStatusConfig() && !isItemClosed(task) ?
            `<button type="button" class="task-quick-close-btn icon-action-btn table-action-btn ml-2 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100" data-task-id="${task.id}" title="Quick close">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
                </svg>
            </button>` :
            '';
        const mobileQuickCloseButtonHtml = getTaskQuickCloseStatusConfig() && !isItemClosed(task) ?
            `<button type="button" class="task-quick-close-btn icon-action-btn table-action-btn flex-shrink-0" data-task-id="${task.id}" title="Quick close" aria-label="Quick close">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"></path>
                </svg>
            </button>` :
            '';

        return `
        <div data-id="${task.id}" class="task-list-item group p-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg mb-3 cursor-pointer transition-colors shadow-sm bg-white dark:bg-gray-800 text-sm">
            <div class="space-y-3 sm:hidden">
                <div class="flex items-start justify-between gap-3">
                    <div class="flex min-w-0 items-start">
                        <span class="mt-1.5 h-2.5 w-2.5 rounded-full mr-2.5 flex-shrink-0" style="background-color: ${priority.color || '#ccc'};" title="Urgency: ${priority.name || 'N/A'}"></span>
                        <span class="font-medium leading-5 text-gray-900 dark:text-gray-100 break-words min-w-0" title="${task.title || ''}">${task.title || 'Untitled'}</span>
                    </div>
                    ${mobileQuickCloseButtonHtml}
                </div>
                <div class="space-y-2 text-xs">
                    ${typeBadgeHtml ? `<div class="flex min-w-0 items-center overflow-hidden">${typeBadgeHtml}</div>` : ''}
                    <div class="flex min-w-0 items-center overflow-hidden"><span class="max-w-full px-2 py-0.5 rounded font-medium ${statusTextColor} text-center truncate" style="background-color: ${status.color};" title="Status: ${status.name}">${status.name}</span></div>
                    <span class="text-gray-500 dark:text-gray-400 flex items-center min-w-0" title="Required By: ${task.due_date || 'None'}"><span class="material-symbols-outlined text-[14px] mr-1 opacity-70 flex-shrink-0">calendar_today</span><span class="truncate">${formatDate(task.due_date) || 'No date'}</span></span>
                    <span class="text-gray-500 dark:text-gray-400 flex items-center min-w-0" title="Assigned/Owner: ${displayUserNames}"><span class="material-symbols-outlined text-[15px] mr-1 opacity-70 flex-shrink-0">person</span><span class="truncate">${displayUserNames}</span></span>
                </div>
            </div>

            <div class="hidden sm:flex sm:items-center">
                <div class="flex items-center w-[300px] flex-shrink-0 mr-4 min-w-0">
                    <span class="w-2.5 h-2.5 rounded-full mr-2.5 flex-shrink-0" style="background-color: ${priority.color || '#ccc'};" title="Urgency: ${priority.name || 'N/A'}"></span>
                    <span class="font-medium text-gray-900 dark:text-gray-100 truncate" title="${task.title || ''}">${task.title || 'Untitled'}</span>
                </div>

                <div class="grid grid-cols-[100px_120px_200px_200px] gap-3 items-center flex-shrink-0 mr-4 text-xs border-r border-gray-100 dark:border-gray-700 pr-4">
                    <span class="text-gray-500 dark:text-gray-400 flex items-center truncate" title="Required By: ${task.due_date || 'None'}"><span class="material-symbols-outlined text-[14px] mr-1 opacity-70 flex-shrink-0">calendar_today</span><span class="truncate">${formatDate(task.due_date) || 'No date'}</span></span>
                    <span class="text-gray-500 dark:text-gray-400 flex items-center truncate" title="Assigned/Owner: ${displayUserNames}"><span class="material-symbols-outlined text-[15px] mr-1 opacity-70 flex-shrink-0">person</span><span class="truncate">${displayUserNames}</span></span>
                    <div class="truncate flex items-center justify-start">${typeBadgeHtml}</div>
                    <div class="truncate flex items-center justify-start"><span class="px-2 py-0.5 rounded font-medium ${statusTextColor} flex-shrink-0 text-center truncate" style="background-color: ${status.color};" title="Status: ${status.name}">${status.name}</span></div>
                </div>

                <div class="flex items-center flex-grow min-w-0 space-x-12">
                    ${checklistHtml ? `<div class="flex-shrink-0 min-w-0 truncate max-w-[40%]">${checklistHtml}</div>` : ''}
                    ${latestNoteHtml ? `<div class="flex-shrink min-w-0 truncate flex-grow">${latestNoteHtml}</div>` : ''}
                </div>
                ${quickCloseButtonHtml}
            </div>
        </div>
        `;
    };

    const parentDataset = itemType ? `data-${itemType}-id="${parentId}"` : '';
    const parentItem = findItem(parentId);
    const showQuickAddTask = !!parentDataset && !isItemClosed(parentItem);
    const closedSummaryClasses = closedExpanded
        ? `${CLOSED_TASK_SUMMARY_BUTTON_CLASSES} text-[var(--dfc-brand-accent)]`
        : `${CLOSED_TASK_SUMMARY_BUTTON_CLASSES} text-gray-500 hover:text-[var(--dfc-brand-primary)] dark:text-gray-400`;

    return `
        <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-6 mt-6">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-semibold dark:text-gray-100">Tasks</h2>
                ${showQuickAddTask ? `<button type="button" class="quick-add-task-btn" ${parentDataset} data-form-type="task" title="Add Task" aria-label="Add task">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                </button>` : ''}
            </div>
            <div>
                <div class="mb-4">
                    ${openTasks.map(createTaskListItem).join('')}
                </div>
                ${closedTasks.length > 0 ? `
                <button type="button" class="${closedSummaryClasses}" aria-expanded="${closedExpanded ? 'true' : 'false'}">
                    <span class="material-symbols-outlined text-[18px] leading-none">${closedExpanded ? 'expand_less' : 'expand_more'}</span>
                    <span>${closedTasks.length} closed tasks</span>
                </button>
                ` : ''}
                ${closedTasks.length > 0 && closedExpanded ? `
                <div class="closed-linked-tasks opacity-70">
                    ${closedTasks.map(createTaskListItem).join('')}
                </div>
                ` : ''}
            </div>
        </div>
        `;
}

/**
 * Attaches listeners for linked task section UI controls.
 * @param {HTMLElement} container Detail view container.
 * @param {string} parentId Parent item id.
 * @param {object} callbacks Listener callbacks.
 * @param {Function} callbacks.renderDetailsView Re-renders the active detail view.
 */
export function attachLinkedTaskSectionListeners(
    container,
    parentId,
    { renderDetailsView },
) {
    const closedSummary = container.querySelector('.closed-linked-tasks-summary');
    if (!closedSummary) {return;}

    closedSummary.addEventListener('click', () => {
        const uiSession = getLinkedTaskUiSession(parentId);
        uiSession.closedExpanded = !uiSession.closedExpanded;
        renderDetailsView();
    });
}
